// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Browser } from "@capacitor/browser";
import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { FullScreenDialog, useToast } from "@src/appV2/lib";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useHelloSignEmbeddedFlow } from "@src/appV2/redesign/Documents/Details/Workflows/HelloSign/useHelloSignEmbeddedFlow";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { invalidateWorkerRequirements } from "../../../api/invalidateWorkerRequirements";
import {
  DOCUMENT_DETAILS_FULL_PATH,
  DOCUMENT_SUBMISSION_APPROVED_PATH,
  DOCUMENT_SUBMISSION_ERROR_PATH,
  DOCUMENT_SUBMISSION_IN_PROGRESS_PATH,
  DOCUMENT_SUBMISSION_IN_REVIEW_PATH,
} from "../../../paths";
import { DocumentUploadFooter } from "../../Footer";
import { DocumentDetailsInstructions } from "../../Instructions";
import { DocumentDetailsMetadata } from "../../Metadata";
import { DocumentDetailsRequiredBySection } from "../../RequiredBySection/Section";
import { DocumentPageScrollWrapper } from "../../ScrollWrapper";
import { DocumentDetailsStatusCard } from "../../StatusCard";
import { useDocumentDetailsActions } from "../../useDocumentDetailsActions";
import { useRequirementDetailsContext } from "../../useRequirementDetailsContext";
import { useHelloSignFallbackUrl } from "./useHelloSignFallbackUrl";

export function DocumentDetailsSignWithHelloSignPage() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const helloSignIframeContainerRef = useRef<HTMLDivElement>(null);
  const helloSignFormModalState = useModalState();
  const queryClient = useQueryClient();
  const { showInfoToast } = useToast();

  const { status, requirementId, requirement } = workerRequirement;
  const { name } = requirement?.attributes ?? {};

  const { deleteDocument } = useDocumentDetailsActions();
  const shouldUseHelloSignFallbackUrl = useHelloSignFallbackUrl();

  const { isCreatingHelloSignSession, isSubmittingHelloSignDocument, startHelloSignEmbeddedFlow } =
    useHelloSignEmbeddedFlow({
      requirementId,
      onSuccess: (isDocumentApproved) => {
        const modalPath = isDocumentApproved
          ? DOCUMENT_SUBMISSION_APPROVED_PATH
          : DOCUMENT_SUBMISSION_IN_REVIEW_PATH;

        void invalidateWorkerRequirements(queryClient);
        helloSignFormModalState.closeModal();
        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${modalPath}`, {
            requirementId,
          })
        );
      },
      onError: () => {
        helloSignFormModalState.closeModal();
        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
            requirementId,
          })
        );
      },
      helloSignIframeContainerRef,
      onHelloSignClientCancel: () => {
        helloSignFormModalState.closeModal();
      },
    });

  useEffect(() => {
    if (isSubmittingHelloSignDocument) {
      history.replace(
        generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_IN_PROGRESS_PATH}`, {
          requirementId,
        })
      );
    }
  }, [history, isSubmittingHelloSignDocument, requirementId]);

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title={name ?? ""}
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
      />

      <DocumentPageScrollWrapper ref={scrollRef}>
        <DocumentDetailsMetadata />
        <DocumentDetailsStatusCard />
        <DocumentDetailsInstructions />
        <DocumentDetailsRequiredBySection />
      </DocumentPageScrollWrapper>

      <DocumentUploadFooter
        status={status}
        isLoading={isCreatingHelloSignSession}
        onSignButtonClick={async () => {
          if (
            isDefined(requirement?.attributes.helloSignConfig?.fallbackStaticUrl) &&
            (shouldUseHelloSignFallbackUrl ||
              !requirement.attributes.helloSignConfig.isEmbeddedFlowEnabled)
          ) {
            const browserFinishedListener = await Browser.addListener(
              "browserFinished",
              async () => {
                showInfoToast(
                  "If you have signed this document, please check back here later for an update"
                );
                await browserFinishedListener.remove();
              }
            );
            await Browser.open({ url: requirement.attributes.helloSignConfig.fallbackStaticUrl });
          } else {
            helloSignFormModalState.openModal();
            void startHelloSignEmbeddedFlow();
          }
        }}
        onDeleteButtonClick={deleteDocument}
      />

      <FullScreenDialog modalState={helloSignFormModalState}>
        <Box ref={helloSignIframeContainerRef} sx={{ height: "100%" }} />
      </FullScreenDialog>
    </PageWrapper>
  );
}
